import { Logger, LogManager } from "@mcleod/core";
import { GlobalKeyHandlerGroup } from "./GlobalKeyHandlerGroup";
import { Overlay } from "../page/Overlay";
import { Panel } from "../components/panel/Panel";
import { ScreenStack } from "../components/panel/ScreenStack";

export class LogicKeyHandling {
    private static _log: Logger;

    static handleKey = (event: KeyboardEvent) => {
        this.getLog().debug("Attempting to handle key: %o", event);
        // If an overlay is being displayed, that trumps everything.  The key can be handled by that overlay or by a
        // global key listener.
        const currentOverlay = ScreenStack.getNewestOverlay();
        if (currentOverlay != null) {
            this.handleKeyInNewestOverlay(currentOverlay, event);
            return;
        }

        // Next, let the newest non-modal dialog try to handle the key.  The key can be handled by that dialog or by a
        // global key listener.
        const currentNonModalDialog = ScreenStack.getNewestNonModalDialog();
        if (currentNonModalDialog != null) {
            this.handleNonModalDialogKey(currentNonModalDialog, event);
            return;
        }

        // Next, let the oldest snackbar try to handle the key
        const handledByOldestSnackbar = this.handleKeyInOldestSnackbar(event);
        if (handledByOldestSnackbar === true) {
            return;
        }

        // Next, let the newest toast try to handle the key
        const keyHandledByNewestToast = this.handleKeyInNewestToast(event);
        if (keyHandledByNewestToast === true) {
            return;
        }
        
        // If we made it this far, allow the current screen to try to handle the key, unless there is a snackbar or
        // toast present (in which case we want to go straight to the global key handlers).
        const oldestSnackbar = ScreenStack.getOldestSnackbar();
        const newestToast = ScreenStack.getNewestToast();
        if (oldestSnackbar == null && newestToast == null) {
            let handledByScreen = false;
            const currentScreen = ScreenStack.getCurrentScreen();
            this.getLog().debug("Attempting to handle key in current screen: %o", currentScreen);
            if (currentScreen != null) {
                handledByScreen = currentScreen.getKeyHandlerGroup().handleKey(event);
            }
            if (handledByScreen === true) {
                this.getLog().debug("Key handled by current screen: %o", currentScreen);
                return;
            }
        }

        // Finally, let global key handlers try to handle the key
        this.handleKeyWithGlobalGroup(event);
    }

    private static handleKeyInNewestOverlay(overlay: Overlay, event: KeyboardEvent) {
        this.getLog().debug("Attempting to handle key in newest overlay");

        // Let the oldest snackbar try to handle the key
        const handledByOldestSnackbar = this.handleKeyInOldestSnackbar(event);
        if (handledByOldestSnackbar === true) {
            return;
        }

        // Next, let the newest toast try to handle the key
        const keyHandledByNewestToast = this.handleKeyInNewestToast(event);
        if (keyHandledByNewestToast === true) {
            return;
        }

        // Next, let the overlay content itself try to handle the key
        this.getLog().debug("Attempting to handle key in overlay: %o", overlay);
        const handledByOverlay = overlay.getKeyHandlerGroup().handleKey(event);
        if (handledByOverlay === true) {
            this.getLog().debug("Key handled by overlay: %o", overlay);
            return;
        }

        // Finally, let global key handlers try to handle the key
        this.handleKeyWithGlobalGroup(event);
    }

    private static handleNonModalDialogKey(dialog: Panel, event: KeyboardEvent) {
        // Let the non-modal dialog try to handle the key
        this.getLog().debug("Attempting to handle key in non-modal dialog: %o", dialog);
        const handledByDialog = dialog.getKeyHandlerGroup().handleKey(event);
        if (handledByDialog === true) {
            this.getLog().debug("Key handled by non-modal dialog: %o", dialog);
            return;
        }

        // Finally, let global key handlers try to handle the key
        this.handleKeyWithGlobalGroup(event);
    }

    private static handleKeyInOldestSnackbar(event: KeyboardEvent): boolean {
        const oldestSnackbar = ScreenStack.getOldestSnackbar();
        if (oldestSnackbar != null) {
            this.getLog().debug("Attempting to handle key in oldest snackbar: %o", oldestSnackbar);
            const handledBySnackbar = oldestSnackbar.getKeyHandlerGroup().handleKey(event);
            if (handledBySnackbar === true) {
                this.getLog().debug("Key handled by oldest snackbar: %o", oldestSnackbar);
                return true;
            }
        }
        return false;
    }

    private static handleKeyInNewestToast(event: KeyboardEvent): boolean {
        const newestToast = ScreenStack.getNewestToast();
        if (newestToast != null) {
            this.getLog().debug("Attempting to handle key in newest toast: %o", newestToast);
            const handledByToast = newestToast.getKeyHandlerGroup().handleKey(event);
            if (handledByToast === true) {
                this.getLog().debug("Key handled by newest toast: %o", newestToast);
                return true;
            }
        }
        return false;
    }

    private static handleKeyWithGlobalGroup(event: KeyboardEvent): boolean {
        this.getLog().debug("Attempting to handle key with global key group: %o", GlobalKeyHandlerGroup.get());
        const handledByGlobalKeyHandlerGroup = GlobalKeyHandlerGroup.get().handleKey(event);
        if (handledByGlobalKeyHandlerGroup === true) {
            this.getLog().debug("Key handled by global key handler group: %o", GlobalKeyHandlerGroup.get());
        }
        return handledByGlobalKeyHandlerGroup;
    }

    private static getLog(): Logger {
        if (this._log == null) {
            this._log = LogManager.getLogger("components.keyhandling.LogicKeyHandling");
        }
        return this._log;
    }
}
